// Device ID permanente + MAC address. Pra exibir em "Sobre" + suporte.
//
// Estratégia: tenta APIs nativas (Tizen DUID / webOS LGUDID) primeiro —
// essas são gravadas no firmware e NUNCA mudam, mesmo desinstalando o app.
// Fallback web: UUID v4 persistido em 3 lugares redundantes (IndexedDB +
// localStorage + Cache API) — só some se usuário fizer "limpar dados" no
// navegador deliberadamente.
//
// Marca a origem no ID (`tizen-duid:`, `webos-lgudid:`, `web-uuid:`) pra
// suporte saber se é confiável (TV) ou volátil (web).

const STORAGE_KEY = 'iptv:device-id'
const IDB_DB = 'iptv-device'
const IDB_STORE = 'identity'
const CACHE_NAME = 'iptv-device-id'

export interface DeviceIdInfo {
  /** ID permanente prefixado pela origem (tizen-duid: / webos-lgudid: / web-uuid:).
   *  Usado em headers/auth/logs — é o ID "real" pra disputa de licença. */
  id: string
  /** Versão curta humana-friendly (8-12 chars) pra exibir na UI. Ex: "TV-A3F2C9D1".
   *  Deriva determinística do `id` — mesmo dispositivo sempre mostra mesma string. */
  shortId: string
  /** Origem real do ID — usar pra explicar ao usuário se é fixo ou pode trocar. */
  source: 'tizen' | 'webos' | 'android' | 'web'
  /** Firmware grava → permanente real. Web → "muito persistente" mas não inviolável. */
  permanent: boolean
}

let cached: DeviceIdInfo | null = null

// P2 (perf/tv-hero-coldstart): single-flight da PROMISE em voo, não só do valor
// resolvido. No boot frio, callers concorrentes (TrialGate → api.trialStatus e o
// 1º signStream, ambos via getDeviceInfo) chamavam `getDeviceId()` antes de
// `cached` existir — e cada um refazia IDB + Cache API + Luna (webOS) em
// duplicata. Guardando a promise em voo, N chamadas concorrentes = 1 resolução.
let inflight: Promise<DeviceIdInfo> | null = null

/**
 * Retorna o Device ID. Resolve uma vez e cacheia em memória pro resto da
 * sessão (operação assíncrona pelas APIs nativas + IDB). Single-flight: enquanto
 * a 1ª resolução está em voo, chamadas concorrentes recebem a MESMA promise.
 */
export function getDeviceId(): Promise<DeviceIdInfo> {
  if (cached) return Promise.resolve(cached)
  if (inflight) return inflight
  // IIFE async + try/finally (construção de linguagem, ES5-safe): `.finally` de
  // Promise é Chr63+, abaixo do piso da TV (Chr38/53). Limpa o slot ao terminar
  // pra que uma falha transitória (não deveria: resolveDeviceId sempre resolve)
  // não deixe a próxima chamada presa numa promise rejeitada.
  inflight = (async (): Promise<DeviceIdInfo> => {
    try {
      return await resolveDeviceId()
    } finally {
      inflight = null
    }
  })()
  return inflight
}

async function resolveDeviceId(): Promise<DeviceIdInfo> {
  if (cached) return cached

  // 1. Tizen (Samsung TV)
  const tizenDuid = tryTizenDuid()
  if (tizenDuid) {
    const id = `tizen-duid:${tizenDuid}`
    cached = { id, shortId: makeShortId(id, 'TV'), source: 'tizen', permanent: true }
    return cached
  }

  // 2. webOS (LG TV)
  const webosId = await tryWebosId()
  if (webosId) {
    const id = `webos-lgudid:${webosId}`
    cached = { id, shortId: makeShortId(id, 'TV'), source: 'webos', permanent: true }
    return cached
  }

  // 3. Android TV (via UA — não tem API direta de ID em WebView).
  //    Sem ID nativo confiável, cai no fallback web mas marca como Android.
  //    (Apps nativos Android leem Settings.Secure.ANDROID_ID — fora do nosso escopo aqui.)

  // 4. Web/PWA fallback: UUID v4 persistido redundantemente
  const webId = await getOrCreateWebId()
  const id = `web-uuid:${webId}`
  cached = { id, shortId: makeShortId(id, 'WEB'), source: 'web', permanent: false }
  return cached
}

/**
 * Gera ID curto humano (`PREFIX-XXXXXXXX`) determinístico a partir do ID
 * longo. Hash FNV-1a 32-bit → 8 hex chars. Mesmo input sempre = mesmo output.
 * Não é seguro contra colisão maliciosa, mas pra display/suporte é ótimo.
 */
function makeShortId(longId: string, prefix: string): string {
  let hash = 0x811c9dc5
  for (let i = 0; i < longId.length; i++) {
    hash ^= longId.charCodeAt(i)
    hash = Math.imul(hash, 0x01000193)
  }
  const hex = (hash >>> 0).toString(16).toUpperCase().padStart(8, '0')
  return `${prefix}-${hex}`
}

/**
 * MAC address. Disponível só em TVs nativas (Tizen/webOS expõem). Web
 * retorna null por restrição de segurança do navegador.
 */
export async function getMacAddress(): Promise<string | null> {
  // Tizen — webapis.network.getMac() (síncrono)
  try {
    const w = window as unknown as {
      webapis?: { network?: { getMac?: () => string } }
    }
    const mac = w.webapis?.network?.getMac?.()
    if (mac && typeof mac === 'string' && mac !== '00:00:00:00:00:00') {
      return mac.toUpperCase()
    }
  } catch {
    // sem webapis ou erro — segue
  }

  // webOS — luna service "connectionmanager" expõe MAC
  try {
    const lunaMac = await tryWebosMac()
    if (lunaMac) return lunaMac.toUpperCase()
  } catch {
    // sem webOS ou serviço indisponível — null
  }

  return null
}

// ===== Helpers privados =====

function tryTizenDuid(): string | null {
  try {
    const w = window as unknown as {
      webapis?: { productinfo?: { getDuid?: () => string } }
    }
    const duid = w.webapis?.productinfo?.getDuid?.()
    if (duid && typeof duid === 'string' && duid.length > 0) return duid
  } catch {
    // sem webapis (não é Tizen)
  }
  return null
}

interface WebosLuna {
  service?: {
    request?: (
      uri: string,
      args: {
        method: string
        parameters?: Record<string, unknown>
        onSuccess?: (res: unknown) => void
        onFailure?: (err: unknown) => void
      },
    ) => void
  }
}

function getWebosBridge(): WebosLuna | null {
  const w = window as unknown as { webOS?: WebosLuna; PalmServiceBridge?: WebosLuna }
  return w.webOS ?? w.PalmServiceBridge ?? null
}

async function tryWebosId(): Promise<string | null> {
  const bridge = getWebosBridge()
  if (!bridge?.service?.request) return null
  return new Promise<string | null>((resolve) => {
    const timeout = setTimeout(() => resolve(null), 3000)
    try {
      bridge.service!.request!('luna://com.webos.service.sm', {
        method: 'deviceid/getIDs',
        parameters: { idType: ['LGUDID'] },
        onSuccess: (res) => {
          clearTimeout(timeout)
          // res = { idList: [{ idType: 'LGUDID', idValue: '...' }], returnValue: true }
          const list = (res as { idList?: Array<{ idType: string; idValue: string }> }).idList
          const item = list?.find((x) => x.idType === 'LGUDID')
          resolve(item?.idValue ?? null)
        },
        onFailure: () => {
          clearTimeout(timeout)
          resolve(null)
        },
      })
    } catch {
      clearTimeout(timeout)
      resolve(null)
    }
  })
}

async function tryWebosMac(): Promise<string | null> {
  const bridge = getWebosBridge()
  if (!bridge?.service?.request) return null
  return new Promise<string | null>((resolve) => {
    const timeout = setTimeout(() => resolve(null), 3000)
    try {
      bridge.service!.request!('luna://com.webos.service.connectionmanager', {
        method: 'getStatus',
        onSuccess: (res) => {
          clearTimeout(timeout)
          const r = res as {
            wired?: { macAddress?: string }
            wifi?: { macAddress?: string }
          }
          resolve(r.wired?.macAddress ?? r.wifi?.macAddress ?? null)
        },
        onFailure: () => {
          clearTimeout(timeout)
          resolve(null)
        },
      })
    } catch {
      clearTimeout(timeout)
      resolve(null)
    }
  })
}

async function getOrCreateWebId(): Promise<string> {
  // Tenta ler dos 3 storages, preferindo o mais persistente. Se achar em
  // qualquer um, replica nos outros (auto-heal — se o usuário limpou só
  // um, restaura dos outros).
  const fromIdb = await readIdb()
  const fromLs = readLocalStorage()
  const fromCache = await readCache()

  const found = fromIdb ?? fromLs ?? fromCache
  if (found) {
    // Replica nos que estão vazios pra auto-healing futuro
    if (!fromIdb) await writeIdb(found).catch(() => {})
    if (!fromLs) writeLocalStorage(found)
    if (!fromCache) await writeCache(found).catch(() => {})
    return found
  }

  // Nada existe — gera novo
  const fresh = generateUuidV4()
  await writeIdb(fresh).catch(() => {})
  writeLocalStorage(fresh)
  await writeCache(fresh).catch(() => {})
  return fresh
}

/**
 * UUID v4 seguro pra TVs antigas. `crypto.randomUUID` só existe em Chromium 92+
 * (Tizen 4+/webOS 4+); no WebView Chr38 (webOS 2017) e Chr53 (webOS 2019) a
 * chamada direta lança `TypeError: crypto.randomUUID is not a function`. Este
 * helper faz feature-detect e cai num gerador Math.random (suficiente pra IDs
 * LOCAIS — device id, profile id). Exportado pra ser a ÚNICA fonte de UUID no
 * app TV: qualquer call-site que chamava `crypto.randomUUID()` direto quebrava
 * no device antigo (ex: local-db.ts na criação de perfil).
 */
export function generateUuidV4(): string {
  // crypto.randomUUID disponível em browsers modernos + Tizen 4+/webOS 4+
  if (typeof crypto !== 'undefined' && 'randomUUID' in crypto) {
    return crypto.randomUUID()
  }
  // Fallback manual (pra TVs antigas) — usa Math.random (suficiente pra ID local)
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, (c) => {
    const r = (Math.random() * 16) | 0
    const v = c === 'x' ? r : (r & 0x3) | 0x8
    return v.toString(16)
  })
}

function readLocalStorage(): string | null {
  try {
    return localStorage.getItem(STORAGE_KEY)
  } catch {
    return null
  }
}

function writeLocalStorage(id: string): void {
  try {
    localStorage.setItem(STORAGE_KEY, id)
  } catch {
    // alguns ambientes (Safari private) bloqueiam
  }
}

async function readIdb(): Promise<string | null> {
  return new Promise((resolve) => {
    try {
      const req = indexedDB.open(IDB_DB, 1)
      req.onupgradeneeded = () => {
        req.result.createObjectStore(IDB_STORE)
      }
      req.onsuccess = () => {
        const db = req.result
        const tx = db.transaction(IDB_STORE, 'readonly')
        const store = tx.objectStore(IDB_STORE)
        const getReq = store.get('id')
        getReq.onsuccess = () => {
          resolve((getReq.result as string) ?? null)
          db.close()
        }
        getReq.onerror = () => {
          resolve(null)
          db.close()
        }
      }
      req.onerror = () => resolve(null)
    } catch {
      resolve(null)
    }
  })
}

async function writeIdb(id: string): Promise<void> {
  return new Promise((resolve, reject) => {
    try {
      const req = indexedDB.open(IDB_DB, 1)
      req.onupgradeneeded = () => {
        req.result.createObjectStore(IDB_STORE)
      }
      req.onsuccess = () => {
        const db = req.result
        const tx = db.transaction(IDB_STORE, 'readwrite')
        tx.objectStore(IDB_STORE).put(id, 'id')
        tx.oncomplete = () => {
          resolve()
          db.close()
        }
        tx.onerror = () => {
          reject(tx.error)
          db.close()
        }
      }
      req.onerror = () => reject(req.error)
    } catch (err) {
      reject(err)
    }
  })
}

async function readCache(): Promise<string | null> {
  try {
    if (!('caches' in window)) return null
    const cache = await caches.open(CACHE_NAME)
    const res = await cache.match('/__device-id__')
    if (!res) return null
    return await res.text()
  } catch {
    return null
  }
}

async function writeCache(id: string): Promise<void> {
  try {
    if (!('caches' in window)) return
    const cache = await caches.open(CACHE_NAME)
    await cache.put('/__device-id__', new Response(id, { headers: { 'content-type': 'text/plain' } }))
  } catch {
    // ignora
  }
}
